"use client";

import { ArrowRight, ShieldCheck, Ruler, Activity, Weight, Zap, Printer, CircleCheck as CheckCircle, Layers, Shield } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function QualityAssuranceStandards() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const grid = useRevealOnScroll<HTMLDivElement>();
  const banner = useRevealOnScroll<HTMLDivElement>();
  const cta = useRevealOnScroll<HTMLDivElement>();
  const badges = [{
    icon: Layers,
    title: "Film Thickness Control",
    description: "Precision micrometer testing ensures uniform gauge across the entire roll, preventing weak spots and thin areas.",
    label: "Micron Precision"
  }, {
    icon: Ruler,
    title: "Size Measurement",
    description: "Automated dimensional checks verify that every bag meets exact width and length specifications for perfect fit.",
    label: "Exact Specs"
  }, {
    icon: Activity,
    title: "Tear Resistance",
    description: "Elmendorf tear testing measures the force required to propagate a tear, ensuring high durability under stress.",
    label: "ASTM D1922"
  }, {
    icon: Weight,
    title: "Load Capacity",
    description: "Static and dynamic load testing confirms the maximum weight each bag can carry without structural failure.",
    label: "Heavy Duty"
  }, {
    icon: Zap,
    title: "Seal Strength",
    description: "Heat-seal integrity testing ensures the bottom and side seals are stronger than the film itself to prevent leaks.",
    label: "Leak Proof"
  }, {
    icon: Shield,
    title: "Drawstring Testing",
    description: "Rigorous pull-tests on drawstring handles ensure they remain securely attached even at maximum capacity.",
    label: "Handle Integrity"
  }, {
    icon: Printer,
    title: "Print Quality",
    description: "Visual and chemical inspection of logos and text to ensure ink adhesion and high-resolution clarity.",
    label: "HD Printing"
  }, {
    icon: CheckCircle,
    title: "Final Approval",
    description: "A comprehensive multi-point inspection of the finished batch before packaging and global dispatch.",
    label: "QC Passed"
  }];
  const certifications = [{
    label: "ISO Certified"
  }, {
    label: "Industrial Grade"
  }, {
    label: "Eco-Compliant"
  }];
  return <section data-section-id="673" id="quality-assurance-standards" className="relative overflow-hidden bg-background py-32 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-[80%] opacity-40" style={{
      background: "radial-gradient(ellipse 60% 70% at 50% 100%, hsl(25 95% 50% / 0.15), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-7xl">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-sm font-medium tracking-[0.2em] text-primary uppercase font-default">
            Our Commitment to Excellence
          </p>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-[56px] lg:leading-[1.05] font-bold text-foreground font-default tracking-tight">
            Garbage Bag Quality Assurance
          </h2>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto font-default">
            At Akyasco, we combine advanced polymer technology with rigorous quality control to deliver plastic solutions that exceed industrial expectations.
          </p>
        </div>

        {}
        <div ref={grid.ref} className={`mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 ease-out ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {badges.map((badge, i) => {
          const Icon = badge.icon;
          return <Card data-index={i} key={i} className="bg-card border border-border rounded-lg p-8 min-h-[280px] flex flex-col items-center text-center shadow-md hover:border-primary transition-colors duration-300">
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-lg blur-xl opacity-40" style={{
                background: "hsl(25 95% 50% / 0.15)"
              }} aria-hidden="true" />
                  <div className="relative flex items-center justify-center w-[72px] h-[72px] rounded-lg border border-border bg-background">
                    <Icon className="w-9 h-9 text-primary" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-foreground font-default mb-3">
                  {badge.title}
                </h3>
                <p className="text-sm text-muted-foreground font-default leading-relaxed mb-6 flex-1">
                  {badge.description}
                </p>
                <Badge variant="outline" className="bg-accent border-accent-foreground/20 text-accent-foreground font-medium tracking-wide uppercase text-[10px] px-3 py-1 rounded-lg">
                  {badge.label}
                </Badge>
              </Card>;
        })}
        </div>

        {}
        <div ref={banner.ref} className={`mt-16 rounded-lg border border-border bg-secondary p-8 lg:p-12 transition-all duration-700 ease-out ${banner.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {}
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-4 lg:gap-6">
              {certifications.map((cert, i) => <div data-index={i} key={i} className="flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-background">
                  <ShieldCheck className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                  <span className="text-sm font-medium text-muted-foreground font-default tracking-wide">
                    {cert.label}
                  </span>
                </div>)}
            </div>

            {}
            <div className="text-center lg:text-right">
              <p className="text-2xl lg:text-3xl font-semibold text-foreground font-default leading-tight">
                Over 500+ industrial partners.{" "}
                <span className="text-primary">Zero quality defects.</span>
              </p>
            </div>
          </div>
        </div>

        {}
        <div ref={cta.ref} className={`mt-12 text-center transition-all duration-700 ease-out ${cta.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="ghost" size="lg" className="text-primary hover:text-primary/80 hover:bg-primary/10 font-semibold text-base rounded-lg">
            <Link to="/QualityControlPage">
              View our quality standards
              <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}